package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.QaCheck
import dev.lingvo.shared.QaIgnoreDto
import dev.lingvo.shared.QaIgnoresDto
import dev.lingvo.shared.QaRuleDto
import dev.lingvo.shared.QaRulesResponse
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.components.editor.displayName
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LListChecksIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTagsIcon
import dev.lingvo.site.ds.LTrashIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.enterStaggered
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import dev.lingvo.site.state.runProjectAction
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Which QA checks this project enforces, and which merely mention.
 *
 * **This is the escape hatch, and it exists because the approval gate is real.** An ERROR blocks
 * approval, and the default checks fire constantly on perfectly good strings — `SAME_AS_SOURCE` on
 * every brand name, every "OK", every "Wi-Fi". Without somewhere to say "not here", a team's first
 * release is blocked by a tool they bought yesterday and the fix they find is to stop using QA at
 * all.
 *
 * **Extracted from the page so the project-settings tab and the standalone route are the same
 * surface.** Two implementations of a release gate would drift, and the louder copy would win. The
 * two entry points differ only in who reaches them, and that difference is deliberate: every
 * `PROJECT_SETTINGS` tab is ADMIN or OWNER, while `/settings/qa` stays readable by everyone in the
 * project, because a translator staring at a blocked approval needs to know whether it is a check
 * their own team chose to enforce. Editing is gated on [ProjectPrivilege.QA_MANAGEMENT] either way.
 *
 * The seams ([currentRoute], [currentMemberships] and the five transports) exist for the same reason
 * `ProjectReadinessSection`'s do: a browser test can mount the production composable against a
 * verified route it constructed, without a live session or a server.
 */
@Composable
internal fun ProjectQaRulesSettings(
    slug: String,
    /**
     * The project's target languages, or null to read them from the verified route.
     *
     * Null rather than an empty list, because a project with no target language and a project whose
     * route has not resolved yet are different states and the language-exception picker must be
     * absent in only one of them.
     */
    targetLocales: List<String>? = null,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(slug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    loadRules: suspend (String) -> QaRulesResponse = ApiClient::qaRules,
    loadIgnores: suspend (String) -> QaIgnoresDto = ApiClient::qaIgnores,
    updateRule: suspend (QaCheck, Boolean, QaSeverity?, String?) -> Unit = { check, enabled, severity, locale ->
        ApiClient.updateQaRule(slug, check, enabled, severity, locale)
    },
    clearOverride: suspend (QaCheck, String) -> Unit = { check, locale ->
        ApiClient.clearQaRuleOverride(slug, check, locale)
    },
    removeIgnore: suspend (String) -> Unit = { id -> ApiClient.removeQaIgnore(slug, id) },
) {
    val scope = rememberCoroutineScope()
    val verifiedRoute = currentRoute()
    val memberships = currentMemberships()
    val authority = ProjectAuthority.resolve(verifiedRoute?.project, memberships)
    val authoritySubject = verifiedRoute?.authoritySubject(memberships)
    val qaClaim = ProjectActionClaim.capture(verifiedRoute, memberships, ProjectPrivilege.QA_MANAGEMENT)
    val locales = targetLocales
        ?: verifiedRoute?.project?.let { it.locales - it.sourceLocale }.orEmpty()

    var refreshKey by remember(authoritySubject) { mutableStateOf(0) }
    var busy by remember(authoritySubject) { mutableStateOf(false) }
    var message by remember(authoritySubject) { mutableStateOf<String?>(null) }
    // Removing an exemption puts an error back in front of a release and throws away the sentence
    // somebody wrote to justify it — the row's whole value six months later. Every sibling delete in
    // this surface confirms first; this one fired on the click.
    var removingIgnore by remember(authoritySubject) { mutableStateOf<QaIgnoreDto?>(null) }

    // **A token, not a key.** Toggling one QA rule blanked the rule list to a five-row skeleton and
    // re-entered every card — on the switch the reader had just flipped, with their eye still on it.
    // Both loaders reload underneath now, so the list stays put and only its contents change.
    val rules = loadReloadable(authoritySubject, reloadToken = refreshKey) {
        val subject = authoritySubject
        // Still verifying the route. `requireNotNull` turned that ordinary wait into
        // `Async.Failure("Required value was null.")`, which the failure state printed to the reader
        // word for word. The changed key restarts the load once the route resolves.
        if (subject == null) awaitCancellation()
        requestProjectResource(
            key = subject.action,
            expectedSlug = slug,
            memberships = memberships,
            currentRoute = currentRoute,
            currentMemberships = currentMemberships,
        ) { route ->
            loadRules(route.project.slug)
        }
    }
    val ignoresReloadable = loadReloadable(authoritySubject, reloadToken = refreshKey) {
        val subject = authoritySubject
        if (subject == null) awaitCancellation()
        requestProjectResource(
            key = subject.action,
            expectedSlug = slug,
            memberships = memberships,
            currentRoute = currentRoute,
            currentMemberships = currentMemberships,
        ) { route ->
            loadIgnores(route.project.slug)
        }
    }
    val canEdit = authority.canExemptQa

    /**
     * One QA mutation under the captured claim.
     *
     * [onSettled] runs when the request has finished either way, and exists for the removal
     * confirmation: closing the dialog on the press would put the deletion of somebody's written
     * justification behind a screen that had already moved on, which is the exact defect the
     * sibling confirmations in this package were fixed for.
     */
    // `onSettled` comes first so a trailing lambda still binds to `block`: Kotlin gives a trailing
    // lambda to the LAST parameter, and every other call site here is `mutate { … }`.
    fun mutate(onSettled: () -> Unit = {}, block: suspend () -> Unit) {
        val claim = qaClaim
            ?.takeIf { it.subject == authoritySubject }
            ?.takeIf { it.remainsAuthorized(currentRoute(), currentMemberships()) }
            ?: run {
                onSettled()
                return
            }
        scope.launch {
            runProjectAction(
                claim = claim,
                currentRoute = currentRoute,
                currentMemberships = currentMemberships,
                setBusy = { busy = it },
                transport = block,
                onSuccess = {
                    refreshKey++
                    message = null
                    onSettled()
                },
                onFailure = { error ->
                    message = error.message?.takeIf(String::isNotBlank)
                        ?: tr(
                            "app.projects.slug.settings.project_qa_rules.couldnt_update_these_qa_settings_retry",
                            "Couldn't update these QA settings. Retry?",
                        )
                    onSettled()
                },
            )
        }
    }

    SettingsSection(
        title = t("app.projects.slug.settings.project_qa_rules.qa_rules", "QA rules"),
        description = t(
            "app.projects.slug.settings.project_qa_rules.what_blocks_a_release_here_and",
            "What blocks a release here, and what only gets mentioned.",
        ),
        // Not `wide`. `SettingsWideMeasure` is for a table, a card grid or a chart; nine stacked
        // checkbox-and-select forms are none of those, and drawing them at 1128 moved the page's
        // right edge ~360px when the reader switched tabs, stranded a 380px severity select in a
        // 1100px card, and parked each "Blocks approval" badge a screen away from the rule title it
        // qualifies. The form measure is the answer every other settings tab already gives.
        icon = { LListChecksIcon() },
    ) {
        // Live, because it is only ever here in answer to a switch the reader just moved or an
        // exemption they just removed.
        //
        // **The reload's failure is surfaced here, or the page lies about what it saved.**
        // `loadReloadable` deliberately keeps a failed re-read out of `state` and holds the previous
        // success, which is what stops the list blanking. The cost is that a save which succeeded
        // and a re-read which then failed leaves the *old* rules on screen: the switch springs back,
        // `message` was cleared by the successful save, and nothing says why. The reader concludes
        // the save failed and flips it again — turning back on the check they meant to turn off.
        (message ?: rules.refreshError?.readable() ?: ignoresReloadable.refreshError?.readable())?.let {
            LAlert(
                t("app.projects.slug.settings.project_qa_rules.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }
        if (!canEdit) {
            LAlert(
                t(
                    "app.projects.slug.settings.project_qa_rules.qa_rules_are_managed_by_an",
                    "QA rules are managed by an owner or admin",
                ),
                t(
                    "app.projects.slug.settings.project_qa_rules.a_check_that_blocks_approval_is",
                    "A check that blocks approval is a release decision, so owners and admins set " +
                        "them. Knowing which ones your team enforces is the point of showing you.",
                ),
                variant = LAlertVariant.INFO,
            )
        }

        LAsyncContent(
            rules.state,
            rows = 5,
            loadingLabel = t("app.projects.slug.settings.project_qa_rules.loading_rules", "Loading rules"),
            onRetry = { refreshKey++ },
        ) { loaded ->
            // The content slot is a single element, so the column that spaced these cards apart from
            // the heading and from each other has to be inside it.
            Column(Modifier.fillMaxWidth().gap(Space.XL)) {
                loaded.rules.forEachIndexed { index, rule ->
                    QaRuleCard(
                        rule = rule,
                        index = index,
                        canEdit = canEdit,
                        busy = busy,
                        targetLocales = locales,
                        onRuleChanged = { enabled, severity ->
                            mutate { updateRule(rule.check, enabled, severity, null) }
                        },
                        onOverrideSet = { locale, enabled, severity ->
                            mutate { updateRule(rule.check, enabled, severity, locale) }
                        },
                        onOverrideCleared = { locale -> mutate { clearOverride(rule.check, locale) } },
                    )
                }

                // Stated where somebody is deciding, because it is the reason to turn something down
                // to a warning rather than off: off means nobody ever hears about it again.
                LMuted(
                    t(
                        "app.projects.slug.settings.project_qa_rules.a_warning_is_still_shown_on",
                        "A warning is still shown on the string and in the QA list. It just does " +
                            "not stop anybody approving.",
                    ),
                )

                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                        LSectionTitle(
                            t("app.projects.slug.settings.project_qa_rules.exempt_strings", "Exempt strings"),
                            icon = { LTagsIcon() },
                        )
                        // Why this exists beside the switches above: it is what lets them stay
                        // strict. A team whose product is called "OK" should not have to demote
                        // SAME_AS_SOURCE everywhere to stop it firing on one string.
                        LMuted(
                            t(
                                "app.projects.slug.settings.project_qa_rules.one_string_one_check_this_is",
                                "One string, one check. This is what lets a check stay an error " +
                                    "when a handful of strings are legitimate exceptions.",
                            ),
                        )
                        LAsyncContent(
                            ignoresReloadable.state,
                            rows = 2,
                            loadingLabel = t(
                                "app.projects.slug.settings.project_qa_rules.loading_exemptions",
                                "Loading exemptions",
                            ),
                            onRetry = { refreshKey++ },
                        ) { loadedIgnores ->
                            QaExemptionList(
                                ignores = loadedIgnores.ignores,
                                canEdit = canEdit,
                                busy = busy,
                                onRemove = { removingIgnore = it },
                            )
                        }
                    }
                }
            }
        }
    }

    val pendingRemoval = removingIgnore
    LConfirmDialog(
        open = pendingRemoval != null,
        title = t("app.projects.slug.settings.project_qa_rules.remove_this_exemption", "Remove this exemption?"),
        confirmLabel = t("app.projects.slug.settings.project_qa_rules.remove_exemption", "Remove exemption"),
        onConfirm = {
            val target = removingIgnore ?: return@LConfirmDialog
            mutate(onSettled = { removingIgnore = null }) { removeIgnore(target.id) }
        },
        onCancel = { removingIgnore = null },
        consequenceTitle = t(
            "app.projects.slug.settings.project_qa_rules.the_check_starts_firing_on_this",
            "The check starts firing on this string again",
        ),
        // One sentence. It was five fragments — a check name, a key, a parenthesised language, a
        // clause and a quoted reason — with the parentheses, the dashes and the quotation marks all
        // in Kotlin where a translator cannot reach them. This is the text somebody reads before
        // removing an exemption, and the quoted reason is the part that is about to be lost for
        // good.
        consequence = pendingRemoval?.let { ignore ->
            trf(
                "app.projects.slug.settings.project_qa_rules.removing_exemption_consequence",
                "{check} on {key} ({language}) goes back to blocking or warning the way the " +
                    "project's rule says, and the reason written here — “{reason}” — is " +
                    "not kept anywhere else.",
                "check" to ignore.check.displayName(),
                "key" to ignore.keyName,
                "language" to (ignore.locale ?: everyLanguage()),
                "reason" to ignore.reason,
            )
        }.orEmpty(),
        busy = busy,
        busyLabel = t(
            "app.projects.slug.settings.project_qa_rules.removing_exemption_busy",
            "Removing exemption…",
        ),
    )
}

/** One check, its explanation, and — for an owner or admin — the two controls that change it. */
@Composable
private fun QaRuleCard(
    rule: QaRuleDto,
    index: Int,
    canEdit: Boolean,
    busy: Boolean,
    targetLocales: List<String>,
    onRuleChanged: (enabled: Boolean, severity: QaSeverity?) -> Unit,
    onOverrideSet: (locale: String, enabled: Boolean, severity: QaSeverity?) -> Unit,
    onOverrideCleared: (locale: String) -> Unit,
) {
    LCard(Modifier.fillMaxWidth().enterStaggered(index)) {
        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                LSectionTitle(
                    rule.check.displayName(),
                    modifier = Modifier.flexGrow(1).styleModifier { property("min-width", "0") },
                )
                if (rule.customised) {
                    LBadge(t("app.projects.slug.settings.project_qa_rules.badge_changed", "Changed"))
                }
                if (rule.blocksApproval) {
                    LBadge(
                        t("app.projects.slug.settings.project_qa_rules.badge_blocks_approval", "Blocks approval"),
                        variant = LBadgeVariant.DANGER,
                        dot = true,
                    )
                } else if (rule.enabled) {
                    LBadge(
                        t("app.projects.slug.settings.project_qa_rules.badge_warns", "Warns"),
                        variant = LBadgeVariant.WARNING,
                        dot = true,
                    )
                } else {
                    LBadge(t("app.projects.slug.settings.project_qa_rules.badge_off", "Off"), dot = true)
                }
            }
            LMuted(rule.check.explanation())

            if (canEdit) {
                LCheckbox(
                    checked = rule.enabled,
                    onCheckedChange = { on -> onRuleChanged(on, rule.severity) },
                    label = t("app.projects.slug.settings.project_qa_rules.run_this_check", "Run this check"),
                    enabled = !busy,
                )
                if (rule.enabled) {
                    LSelect(
                        value = rule.severity.name,
                        options = QaSeverity.entries.map {
                            LSelectOption(
                                it.name,
                                when (it) {
                                    QaSeverity.ERROR ->
                                        t("app.projects.slug.settings.project_qa_rules.severity_error", "Error — blocks approval")
                                    QaSeverity.WARNING ->
                                        t("app.projects.slug.settings.project_qa_rules.severity_warning", "Warning — mentioned, never blocks")
                                },
                            )
                        },
                        onValueChange = { raw ->
                            QaSeverity.entries.firstOrNull { it.name == raw }?.let { severity ->
                                onRuleChanged(true, severity)
                            }
                        },
                        label = t("app.projects.slug.settings.project_qa_rules.severity", "Severity"),
                        enabled = !busy,
                    )

                    // **Where a check is language-shaped.** Japanese does not put a space before a
                    // colon and legitimately ends a sentence with a full-width stop, so a
                    // punctuation rule that is right in German fires on correct Japanese. Without
                    // this the exits were to demote the check everywhere — losing it where it was
                    // right — or to exempt strings one at a time forever.
                    QaLanguageExceptions(
                        rule = rule,
                        locales = targetLocales,
                        busy = busy,
                        onSet = onOverrideSet,
                        onClear = onOverrideCleared,
                    )
                }
            }
        }
    }
}

/** Every string this project has exempted, with the sentence somebody wrote to justify it. */
@Composable
private fun QaExemptionList(
    ignores: List<QaIgnoreDto>,
    canEdit: Boolean,
    busy: Boolean,
    onRemove: (QaIgnoreDto) -> Unit,
) {
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        if (ignores.isEmpty()) {
            LMuted(
                t(
                    "app.projects.slug.settings.project_qa_rules.nothing_is_exempt_exemptions_are_added",
                    "Nothing is exempt. Exemptions are added from the QA list, where you can see " +
                        "the string that tripped the check.",
                ),
            )
            return@Column
        }
        ignores.forEach { ignore ->
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                Column(Modifier.flexGrow(1).gap(Space.XS)) {
                    // A bold line, not a heading: twenty exemptions would put twenty entries in a
                    // screen reader's heading list and bury the two headings this card actually has.
                    Span(Modifier.fontWeight(600).toAttrs()) {
                        Text(
                            tf(
                                "app.projects.slug.settings.project_qa_rules.key_and_check",
                                "{key} — {check}",
                                "key" to ignore.keyName,
                                "check" to ignore.check.displayName(),
                            ),
                        )
                    }
                    // The sentence somebody wrote is the whole value of the row six months later.
                    LMuted(
                        tf(
                            "app.projects.slug.settings.project_qa_rules.exemption_detail",
                            "{scope} · \"{reason}\"{author}",
                            "scope" to (
                                ignore.locale
                                    ?: t("app.projects.slug.settings.project_qa_rules.every_language", "every language")
                            ),
                            "reason" to ignore.reason,
                            "author" to (ignore.createdBy?.let { " — $it" } ?: ""),
                        ),
                    )
                }
                if (canEdit) {
                    LButton(
                        t("app.projects.slug.settings.project_qa_rules.remove", "Remove"),
                        { onRemove(ignore) },
                        variant = LButtonVariant.GHOST,
                        size = LButtonSize.SM,
                        enabled = !busy,
                        // Every row's button reads "Remove"; without this a screen reader hears it
                        // once per exemption with nothing to tell them apart. Naming only the key is
                        // not enough: one string can carry an exemption per check and per language,
                        // which is why the row's own heading carries all three.
                        accessibleLabel = trf(
                            "app.projects.slug.settings.project_qa_rules.remove_exemption_for_key_in_language",
                            "Remove the {check} exemption for {key} in {language}",
                            "check" to ignore.check.displayName(),
                            "key" to ignore.keyName,
                            "language" to (ignore.locale ?: everyLanguage()),
                        ),
                        leadingIcon = { LTrashIcon() },
                    )
                }
            }
        }
    }
}

/**
 * One check's language exceptions: the ones that exist, and a control to add another.
 *
 * **Rendered only under an enabled check**, because an exception to a check nobody runs is a setting
 * with no effect that somebody will later read as one. Every language already carrying an opinion is
 * listed with a way back to the project's answer, and the picker offers only the languages that do
 * not — a second row for a language that has one would be an edit, not an addition, and the two read
 * differently.
 *
 * "Follow the project" is a delete rather than a write, because an override that happens to agree
 * with the project is still an override: it stops following the project the next time somebody
 * changes it.
 */
@Composable
private fun QaLanguageExceptions(
    rule: QaRuleDto,
    locales: List<String>,
    busy: Boolean,
    onSet: (locale: String, enabled: Boolean, severity: QaSeverity?) -> Unit,
    onClear: (locale: String) -> Unit,
) {
    if (locales.isEmpty()) return
    var adding by remember(rule.check) { mutableStateOf("") }

    Column(Modifier.fillMaxWidth().gap(Space.XS)) {
        rule.localeOverrides.forEach { override ->
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                Span(Modifier.flexGrow(1).fontSize(TypeScale.BodySmall).toAttrs()) {
                    Text(localeInfo(override.locale).englishName)
                }
                LBadge(
                    when {
                        !override.enabled -> t("app.projects.slug.settings.project_qa_rules.off_here", "off here")
                        override.severity == QaSeverity.ERROR ->
                            t("app.projects.slug.settings.project_qa_rules.blocks_here", "blocks here")
                        else -> t("app.projects.slug.settings.project_qa_rules.warns_here", "warns here")
                    },
                    variant = when {
                        !override.enabled -> LBadgeVariant.NEUTRAL
                        override.severity == QaSeverity.ERROR -> LBadgeVariant.DANGER
                        else -> LBadgeVariant.WARNING
                    },
                )
                LButton(
                    t("app.projects.slug.settings.project_qa_rules.follow_the_project", "Follow the project"),
                    { onClear(override.locale) },
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                    enabled = !busy,
                    // A check with three language exceptions renders three of these, all reading
                    // "Follow the project". Prefixed with the visible words, not a rephrasing of
                    // them: a speech user says "click Follow the project", and an accessible name
                    // that does not contain that phrase cannot be reached that way. The whole
                    // sentence goes through the catalogue rather than being assembled from a
                    // translated prefix and two untranslated halves.
                    accessibleLabel = trf(
                        "app.projects.slug.settings.project_qa_rules.follow_the_project_for_language_on_check",
                        "Follow the project for {language} on {check}",
                        "language" to localeInfo(override.locale).englishName,
                        "check" to rule.check.displayName(),
                    ),
                )
            }
        }

        val available = locales.filterNot { locale -> rule.localeOverrides.any { it.locale == locale } }
        if (available.isNotEmpty()) {
            LSelect(
                value = adding,
                options = listOf(
                    LSelectOption(
                        "",
                        t("app.projects.slug.settings.project_qa_rules.add_a_language_exception", "Add a language exception…"),
                    ),
                ) + available.map { LSelectOption(it, localeInfo(it).englishName) },
                onValueChange = { picked ->
                    adding = picked
                    // Added as a warning rather than off: the softer of the two useful answers, and
                    // the row is one control away from either once it exists.
                    if (picked.isNotBlank()) onSet(picked, true, QaSeverity.WARNING)
                    adding = ""
                },
                label = t("app.projects.slug.settings.project_qa_rules.language_exceptions", "Language exceptions"),
                enabled = !busy,
            )
        }
    }
}

/** "Every language" as one phrase, so the two sentences that quote it cannot drift apart. */
private fun everyLanguage(): String =
    tr("app.projects.slug.settings.project_qa_rules.every_language_scope", "every language")

/**
 * What each check catches, and why it defaults the way it does.
 *
 * Written as consequences rather than as rule names: somebody turning `SAME_AS_SOURCE` down to a
 * warning needs to know it fires on every brand name, not that it compares two strings.
 */
private fun QaCheck.explanation(): String = when (this) {
    QaCheck.EMPTY -> tr(
        "app.projects.slug.settings.project_qa_rules.explain_empty",
        "Nothing was translated. An error, because an empty string ships as blank UI.",
    )
    QaCheck.PLACEHOLDER_MISMATCH -> tr(
        "app.projects.slug.settings.project_qa_rules.explain_placeholder_mismatch",
        "The translation has different placeholders from the source. An error: this is the one " +
            "that crashes the app at runtime, on the device, in front of the customer.",
    )
    QaCheck.MISSING_PLURAL_FORM -> tr(
        "app.projects.slug.settings.project_qa_rules.explain_missing_plural_form",
        "A language's plural categories are not all filled. An error, because a half-filled " +
            "Russian plural looks finished and breaks only for certain numbers.",
    )
    QaCheck.TAG_MISMATCH -> tr(
        "app.projects.slug.settings.project_qa_rules.explain_tag_mismatch",
        "The markup does not match the source. An error: a dropped closing tag can swallow the " +
            "rest of the screen.",
    )
    QaCheck.LENGTH_EXCEEDED -> tr(
        "app.projects.slug.settings.project_qa_rules.explain_length_exceeded",
        "Longer than the key's limit. A warning by default, because German needs roughly 30% more " +
            "room than the English that set the number — set a per-locale limit rather than " +
            "raising this to an error.",
    )
    QaCheck.LEADING_TRAILING_WHITESPACE -> tr(
        "app.projects.slug.settings.project_qa_rules.explain_whitespace",
        "A space at the start or end. Usually a paste artefact, occasionally deliberate.",
    )
    QaCheck.SAME_AS_SOURCE -> tr(
        "app.projects.slug.settings.project_qa_rules.explain_same_as_source",
        "Identical to the source. Fires on every brand name, every \"OK\", every \"Wi-Fi\" — " +
            "which is why it warns rather than blocks.",
    )
    QaCheck.GLOSSARY_VIOLATION -> tr(
        "app.projects.slug.settings.project_qa_rules.explain_glossary_violation",
        "A glossary term was not translated the way the glossary says. Worth raising to an error " +
            "once your glossary is settled.",
    )
    QaCheck.WRONG_QUOTE_STYLE -> tr(
        "app.projects.slug.settings.project_qa_rules.explain_wrong_quote_style",
        "A straight quote in a language whose typography expects something else. A warning: " +
            "inside a code sample or a measurement (5\" pipe) a straight quote is correct.",
    )
}
